import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, FILE_URL, PRIVACY_URL, TERMS_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { manageSubscription, restorePurchases } from "@/lib/purchases";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Settings() {
  const { user, refresh, signOut } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return <div className="p-10 text-center text-zinc-400">Loading…</div>;

  const set = (patch) => setS({ ...s, ...patch });
  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };
  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      const response = await api.post("/settings/logo", body);
      set({ logo_url: response.data.logo_url });
      toast.success("Logo saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't upload that logo.");
    }
  };
  const removeAccount = async () => {
    setDeleting(true);
    try {
      const response = await api.delete("/auth/account", { data: { confirmation: "DELETE", password } });
      toast.success(response.data.message);
      signOut();
      nav("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't delete the account.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-5 pt-7 pb-3">
        <h1 className="font-heading text-3xl font-black tracking-tight">Settings</h1>
        <p className="text-sm text-zinc-500">Your business info appears on every invoice.</p>
      </header>

      <div className="px-4 space-y-5">
        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <F label="Business Name"><Input data-testid="set-business-name" value={s.business_name} onChange={(e) => set({ business_name: e.target.value })} className="h-12" /></F>
          <F label="Owner Name"><Input data-testid="set-owner" value={s.owner_name} onChange={(e) => set({ owner_name: e.target.value })} className="h-12" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Phone"><Input data-testid="set-phone" value={s.phone} onChange={(e) => set({ phone: e.target.value })} className="h-12" /></F>
            <F label="Email"><Input data-testid="set-email" value={s.email} onChange={(e) => set({ email: e.target.value })} className="h-12" /></F>
          </div>
          <F label="Address"><Textarea data-testid="set-address" value={s.address} onChange={(e) => set({ address: e.target.value })} rows={2} /></F>
          <F label="Payment details">
            <Textarea data-testid="set-payment" value={s.payment_instructions || ""} onChange={(e) => set({ payment_instructions: e.target.value })} rows={3} placeholder="How clients can pay: check, Venmo, Zelle, card link…" />
          </F>
          <F label="Logo">
            <div className="flex items-center gap-3">
              {s.logo_url ? <img src={FILE_URL(s.logo_url)} alt="" className="h-12 w-12 rounded-lg object-cover border border-zinc-200" /> : <div className="h-12 w-12 rounded-lg bg-zinc-100" />}
              <Input data-testid="set-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="h-12" />
            </div>
          </F>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Pricing Defaults</p>
          <div className="grid grid-cols-3 gap-3">
            <F label="Labor $/hr"><Input data-testid="set-hourly" type="number" value={s.hourly_rate} onChange={(e) => set({ hourly_rate: Number(e.target.value) })} className="h-12" /></F>
            <F label="Markup %"><Input data-testid="set-markup" type="number" value={s.default_markup} onChange={(e) => set({ default_markup: Number(e.target.value) })} className="h-12" /></F>
            <F label="Tax %"><Input data-testid="set-tax" type="number" value={s.default_tax_rate} onChange={(e) => set({ default_tax_rate: Number(e.target.value) })} className="h-12" /></F>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Service Area</p>
          <div className="grid grid-cols-2 gap-3">
            <F label="Business ZIP"><Input data-testid="set-zip" value={s.business_zip || ""} onChange={(e) => set({ business_zip: e.target.value })} className="h-12" /></F>
            <F label="Max Radius (mi)"><Input data-testid="set-radius" type="number" value={s.max_radius ?? 25} onChange={(e) => set({ max_radius: Number(e.target.value) })} className="h-12" /></F>
          </div>
          <F label="Primary Service Area"><Input data-testid="set-area" value={s.service_area || ""} onChange={(e) => set({ service_area: e.target.value })} placeholder="e.g. Portland, OR metro" className="h-12" /></F>
          <F label="Trades / Services (comma separated)"><Input data-testid="set-trades" value={(s.trades || []).join(", ")} onChange={(e) => set({ trades: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="plumbing, electrical, HVAC, carpentry" className="h-12" /></F>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Pricing Preference</p>
          <p className="text-xs text-zinc-500">How aggressively recommended prices sit within the local market range.</p>
          <select data-testid="set-strategy" value={s.pricing_strategy || "average"} onChange={(e) => set({ pricing_strategy: e.target.value })} className="h-12 w-full border border-zinc-200 rounded-lg px-3 bg-white text-sm font-medium">
            <option value="budget">Budget / lower-market</option>
            <option value="competitive">Competitive</option>
            <option value="average">Average-market</option>
            <option value="premium">Premium</option>
            <option value="custom">Custom % of market average</option>
          </select>
          {s.pricing_strategy === "custom" && (
            <F label="Custom % of market average"><Input data-testid="set-custompct" type="number" value={s.custom_pct ?? 100} onChange={(e) => set({ custom_pct: Number(e.target.value) })} className="h-12" /></F>
          )}
        </section>

        <Button data-testid="save-settings-btn" onClick={save} className="w-full h-12 font-bold text-base">Save Settings</Button>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Subscription</p>
          <p className="text-sm text-zinc-600">
            {user?.entitled ? `Access is active${user.subscription?.plan ? ` · ${user.subscription.plan}` : ""}.` : "Your trial has ended."}
            {user?.trial_ends_at && !user.subscription?.provider ? ` Trial ends ${new Date(user.trial_ends_at).toLocaleDateString()}.` : ""}
          </p>
          <Button type="button" variant="outline" className="w-full h-11" onClick={() => nav("/paywall")}>View plans</Button>
          <Button type="button" variant="outline" data-testid="settings-restore" className="w-full h-11" onClick={async () => {
            try { await restorePurchases(); await refresh(); toast.success("Purchases checked"); }
            catch (error) { toast.error(error.response?.data?.detail || "Couldn't restore purchases."); }
          }}>Restore Purchases</Button>
          <Button type="button" variant="outline" className="w-full h-11" onClick={() => manageSubscription(user).catch((error) => toast.error(error.response?.data?.detail || "Manage the subscription in your store account."))}>Manage subscription</Button>
          <p className="text-[11px] text-zinc-500">Cancel at least 24 hours before renewal in Apple or Google subscription settings. Deleting your account does not cancel a store subscription.</p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-400">Account</p>
          <p className="text-sm text-zinc-600">{user?.email}</p>
          <p className="text-xs flex gap-4">
            <a className="text-orange-600 font-bold" href={TERMS_URL}>Terms</a>
            <a className="text-orange-600 font-bold" href={PRIVACY_URL}>Privacy</a>
          </p>
          <Button data-testid="logout-button" type="button" variant="outline" className="w-full h-11" onClick={() => { signOut(); nav("/login"); }}>Sign out</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-account-button" type="button" variant="outline" className="w-full h-11 text-red-600 border-red-200">Delete account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your Job Spoken account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes your login and the clients, invoices, receipts, properties, and stock stored for this account. It does not cancel an Apple or Google subscription. Cancel that in the store or it will keep renewing.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {user?.auth_provider !== "google" && user?.auth_provider !== "apple" && (
                <Input data-testid="delete-account-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
              )}
              <AlertDialogFooter>
                <AlertDialogCancel>Keep account</AlertDialogCancel>
                <AlertDialogAction data-testid="delete-account-confirm" onClick={removeAccount} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                  {deleting ? "Deleting…" : "Delete account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><label className="text-[11px] tracking-[0.12em] uppercase font-bold text-zinc-400 block mb-1">{label}</label>{children}</div>
);
